import { useCallback, useEffect, useState } from 'react';

interface VirtualKeyboard {
  overlaysContent: boolean;
  boundingRect?: DOMRectReadOnly;
  show: () => void;
  hide: () => void;
  addEventListener: (type: 'geometrychange', listener: () => void) => void;
  removeEventListener: (type: 'geometrychange', listener: () => void) => void;
}

declare global {
  interface Navigator {
    virtualKeyboard?: VirtualKeyboard;
  }
}

const getVirtualKeyboard = (): VirtualKeyboard | null => {
  try {
    return navigator.virtualKeyboard ?? null;
  } catch {
    return null;
  }
};

export interface StationVirtualKeyboard {
  /** Height the keyboard covers, so the caller can lift its own UI clear of it */
  keyboardHeight: number;
  showKeyboard: (element?: HTMLElement | null) => void;
}

/**
 * Opens the kiosk's on-screen keyboard and reports how much room it takes.
 *
 * Only switch this on while something is actually being typed into. It asks the
 * browser to lay the keyboard over the page instead of shrinking the viewport,
 * so any UI that doesn't respond to `keyboardHeight` would end up underneath it.
 *
 * The API is Chromium-only and needs a secure context, so every call is guarded.
 * Where it is missing, focusing the field still opens the keyboard as before.
 */
export const useStationVirtualKeyboard = (
  active: boolean,
): StationVirtualKeyboard => {
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    if (!active) return;

    const keyboard = getVirtualKeyboard();
    if (!keyboard) return;

    let previousOverlaysContent: boolean;
    try {
      previousOverlaysContent = keyboard.overlaysContent;
      keyboard.overlaysContent = true;
    } catch {
      return;
    }

    const handleGeometryChange = () => {
      try {
        setKeyboardHeight(keyboard.boundingRect?.height ?? 0);
      } catch {
        setKeyboardHeight(0);
      }
    };

    try {
      keyboard.addEventListener('geometrychange', handleGeometryChange);
    } catch {
      // Nothing to listen to, so the caller simply never lifts its UI
    }

    return () => {
      try {
        keyboard.removeEventListener('geometrychange', handleGeometryChange);
      } catch {
        // Already gone
      }
      try {
        keyboard.overlaysContent = previousOverlaysContent;
      } catch {
        // Nothing to restore
      }
      setKeyboardHeight(0);
    };
  }, [active]);

  const showKeyboard = useCallback((element?: HTMLElement | null) => {
    try {
      element?.focus();
    } catch {
      // A missing field is not worth failing over
    }
    try {
      getVirtualKeyboard()?.show();
    } catch {
      // Focus alone still opens the keyboard on most kiosks
    }
  }, []);

  return { keyboardHeight, showKeyboard };
};
